import { Injectable } from '@angular/core';
import { distinctUntilChanged, Observable, Subject } from 'rxjs';
import { ApiService } from './api/api.service';
import { LoggingService } from './logging.service';
import { NavigationService } from './navigation.service';
import { BaseModel } from '../types/generated.types';
import { TenantConfigurationUi } from '../types/tenant.type';
import { TextHelper } from '../helpers/text.helper';
import { MapHelper } from '../helpers/map.helper';
import { Platform } from '../enums/generated.enums';
import { ModuleType, PageType, Tenants } from '../enums/page-type.enum';

@Injectable({ providedIn: 'root' })
export class TenantsService {
  private _tenants: BaseModel[] = [];
  private tenantsById = new Map<string, BaseModel>();
  private tenantConfigurationsById = new Map<string, TenantConfigurationUi>();
  private _selectedTenant: BaseModel | null = null;
  private _tenantConfigurations: TenantConfigurationUi[] = [];
  private tenantsSubject = new Subject<void>();
  private selectedTenantIdSubject = new Subject<string>();
  private tenantUrlNamesById = new Map<string, string>();
  private tenantIdsByUrlName = new Map<string, string>();
  public showWorkflows: boolean;

  get tenants(): BaseModel[] {
    return this._tenants;
  }

  get selectedTenant(): BaseModel {
    return this._selectedTenant;
  }

  get selectedTenantId(): string | null {
    return this._selectedTenant?.id;
  }

  get isAllTenantsSelected(): boolean {
    return this._tenants?.length > 1 && this._selectedTenant?.id == null;
  }

  get tenantConfigurations(): TenantConfigurationUi[] {
    return this._tenantConfigurations;
  }

  get tenants$(): Observable<void> {
    return this.tenantsSubject.asObservable();
  }

  get selectedTenantId$(): Observable<string> {
    return this.selectedTenantIdSubject.pipe(distinctUntilChanged());
  }

  get hasBluePrismTenant(): boolean {
    return this._tenantConfigurations.some(t => t.platform === Platform.BluePrism);
  }

  get hasUiPathTenant(): boolean {
    return this._tenantConfigurations.some(t => t.platform === Platform.UiPath);
  }

  constructor(
    private apiService: ApiService,
    private loggingService: LoggingService,
    private navigationService: NavigationService,
  ) { }

  async initializeTenants(): Promise<void> {
    try {
      const tenantsList = await this.apiService.tenants.getTenantsList();
      this._tenants = tenantsList.filter(t => !t.isDeleted);
      this.tenantsById = MapHelper.toDictionary(tenantsList, t => t.id);
      this._selectedTenant = this.tenantsById.get(this._selectedTenant?.id) ?? null;
      this._tenantConfigurations = await this.apiService.tenants.getTenantsConfigurationUi();
      this.tenantConfigurationsById = MapHelper.toDictionary(this.tenantConfigurations, t => t.tenantId);
      const tenantUrlNames = this._tenants?.map(t => ({ id: t.id, urlName: TextHelper.getUrlNameFromLabel(t.label) })) ?? [];
      this.tenantUrlNamesById = MapHelper.toDictionary2(tenantUrlNames, i => i.id, i => i.urlName);
      this.tenantIdsByUrlName = MapHelper.toDictionary2(tenantUrlNames, i => i.urlName, i => i.id);
      this.tenantsSubject.next();
    } catch (error: any) {
      this.loggingService.logException(error);
      this._tenants = [];
    }
  }

  selectTenantByUrlName(tenantUrlName: string | null): void {
    const tenantId = this.tenantIdsByUrlName.get(tenantUrlName);
    if (tenantId === this._selectedTenant?.id) {
      return;
    }

    this.selectTenantById(tenantId);
  }

  async selectTenant(tenantId: string): Promise<void> {
    const isSpecial = tenantId === Tenants.AllTenants || tenantId === Tenants.AllWorkflows;
    this.selectTenantById(isSpecial ? null : tenantId);
    const tenantUrlName = isSpecial ? tenantId : this.tenantUrlNamesById.get(tenantId);
    if (tenantId === Tenants.AllWorkflows) {
      await this.navigationService.navigateToOtherTenant(tenantUrlName, ModuleType.Workflows, PageType.Workflows);
    } else {
      await this.navigationService.navigateToOtherTenant(tenantUrlName);
    }
  }

  getTenantLabel(tenantId: string): string {
    if (!tenantId) return '';
    const tenant = this.tenantsById.get(tenantId);
    return tenant?.label ?? tenantId ?? `Unknown tenant'}`;
  }

  getTenantPlatform(tenantId: string): Platform | null {
    if (!tenantId) return null;
    const tenant = this.tenantConfigurationsById.get(tenantId);
    return tenant?.platform ?? null;
  }

  private selectTenantById(tenantId: string): void {
    this._selectedTenant = this.tenantsById.get(tenantId);
    this.selectedTenantIdSubject.next(tenantId);
  }
}
